import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMemberRoles } from '../hooks/useMemberRoles';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { RoleRadioGroup } from '../components/RoleRadioGroup';
import { CreateAccountModal } from '../components/CreateAccountModal';
import { AddUserModal } from '../components/AddUserModal';
import { MemberWithUser, Role } from '../types/member-roles';
import { apiClient } from '../api/client';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1100px', margin: '0 auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: 0 },
  subtitle: { color: '#666', fontSize: '0.85rem', marginTop: '4px' },
  searchBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  searchInput: {
    width: '320px',
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#666',
    borderBottom: '2px solid #1a6b3c',
    fontWeight: 600,
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    borderBottom: '1px solid #eee',
    color: '#333',
    verticalAlign: 'middle',
  },
  statusLinked: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
  },
  statusUnlinked: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  saveBtn: {
    padding: '5px 14px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    padding: '5px 14px',
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  createBtn: {
    padding: '5px 14px',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    border: '1px solid #f5c6cb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  errorMessage: {
    flex: 1,
  },
  errorActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexShrink: 0,
  },
  retryBtn: {
    padding: '4px 10px',
    backgroundColor: '#fff',
    color: '#d32f2f',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dismissBtn: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    color: '#d32f2f',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    lineHeight: 1,
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
    fontSize: '14px',
  },
  emptyState: {
    color: '#666',
    marginTop: '1rem',
    fontSize: '14px',
  },
  disabledRow: {
    backgroundColor: '#fafafa',
  },
  selfIndicator: {
    fontSize: '11px',
    color: '#888',
    fontStyle: 'italic',
    marginLeft: '4px',
  },
  disabledBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: 600,
    backgroundColor: '#fdecea',
    color: '#d32f2f',
    marginLeft: '8px',
    verticalAlign: 'middle',
  },
};

export function UsersPage() {
  const { user: authUser } = useAuth();
  const { canEdit } = usePageAuthorization('users');
  const {
    loading,
    error,
    clearError,
    retry,
    searchQuery,
    setSearchQuery,
    filteredMembers,
    pendingChanges,
    setPendingRole,
    saveRole,
    createAccount,
    savingIds,
  } = useMemberRoles();

  const [createModalMember, setCreateModalMember] = useState<MemberWithUser | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'all-users'>('members');
  const [allUsers, setAllUsers] = useState<Array<{ id: string; email: string; fullName: string; role: string; createdAt: string; deactivatedAt: string | null; disabledMessage: string | null }>>([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersSearch, setAllUsersSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<{ userId: string; newPassword: string } | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [provisioningResult, setProvisioningResult] = useState<Record<string, any> | null>(null);
  const [disableModalUser, setDisableModalUser] = useState<{ id: string; fullName: string } | null>(null);
  const [disableMessage, setDisableMessage] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [enabling, setEnabling] = useState<string | null>(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState<Record<string, any> | null>(null);
  const [linkTicketUser, setLinkTicketUser] = useState<{ id: string; fullName: string } | null>(null);
  const [linkTicketNumber, setLinkTicketNumber] = useState('');
  const [linkingTicket, setLinkingTicket] = useState(false);
  const [linkTicketResult, setLinkTicketResult] = useState<Record<string, any> | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState<string | null>(null);
  const [roleSavingId, setRoleSavingId] = useState<string | null>(null);

  const currentUserId = authUser?.sub ?? null;

  async function handleAddUser(payload: { fullName: string; email: string; role: Role; password: string }) {
    setAddUserLoading(true);
    try {
      await apiClient('/users', { method: 'POST', body: payload });
      setAddUserOpen(false);
      setAddUserSuccess(`Account created for ${payload.fullName} (${payload.email}). They can log in with the email and password you set.`);
      fetchAllUsers();
    } finally {
      setAddUserLoading(false);
    }
  }

  // Fetch all system users
  function fetchAllUsers() {
    setAllUsersLoading(true);
    apiClient<Array<{ id: string; email: string; fullName: string; role: string; createdAt: string; deactivatedAt: string | null; disabledMessage: string | null }>>('/users')
      .then((data) => setAllUsers(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setAllUsersLoading(false));
  }

  useEffect(() => {
    if (activeTab !== 'all-users') return;
    fetchAllUsers();
  }, [activeTab]);

  // Backend enforces role via RolesGuard — no frontend access check needed
  // If the API rejects the request, the error banner will show the message

  async function handleCreateAccount(payload: { memberId: string; role: Role; password: string }) {
    setCreateLoading(true);
    try {
      await createAccount(payload);
      setCreateModalMember(null);
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleResetPassword(userId: string) {
    setResettingId(userId);
    setResetResult(null);
    try {
      const result = await apiClient<{ userId: string; newPassword: string }>(`/users/${userId}/reset-password`, {
        method: 'PATCH',
      });
      setResetResult(result);
    } catch (err) {
      alert('Failed to reset password. Please try again.');
    } finally {
      setResettingId(null);
    }
  }

  async function handleCheckProvisioning(userId: string) {
    // Toggle: if result is already showing for this user, dismiss it
    if (provisioningResult && provisioningResult.userId === userId) {
      setProvisioningResult(null);
      return;
    }
    setCheckingId(userId);
    setProvisioningResult(null);
    try {
      const result = await apiClient<Record<string, any>>(`/users/${userId}/debug-chain`);
      setProvisioningResult({ ...result, userId });
    } catch (err) {
      // Fallback to old endpoint if debug-chain fails (role issue)
      try {
        const result = await apiClient<Record<string, any>>(`/users/${userId}/provisioning-status`);
        setProvisioningResult(result);
      } catch {
        setProvisioningResult({ userId, issue: 'Failed to fetch status', canLoginWithTicket: false });
      }
    } finally {
      setCheckingId(null);
    }
  }

  async function handleDisableUser() {
    if (!disableModalUser) return;
    setDisabling(true);
    try {
      await apiClient(`/users/${disableModalUser.id}/disable`, {
        method: 'PATCH',
        body: { disabledMessage: disableMessage.trim() || undefined },
      });
      setDisableModalUser(null);
      setDisableMessage('');
      fetchAllUsers();
    } catch (err: any) {
      alert(err?.message || 'Failed to disable account.');
    } finally {
      setDisabling(false);
    }
  }

  async function handleEnableUser(userId: string) {
    if (!confirm('Are you sure you want to re-enable this account?')) return;
    setEnabling(userId);
    try {
      await apiClient(`/users/${userId}/enable`, { method: 'PATCH' });
      fetchAllUsers();
    } catch (err: any) {
      alert(err?.message || 'Failed to enable account.');
    } finally {
      setEnabling(null);
    }
  }

  async function handleMergeAccounts() {
    if (!mergeSearch.trim()) return;
    setMerging(true);
    setMergeResult(null);
    try {
      const result = await apiClient<Record<string, any>>('/users/merge-duplicate-accounts', {
        method: 'POST',
        body: { nameSearch: mergeSearch.trim() },
      });
      setMergeResult(result);
      fetchAllUsers();
    } catch (err: any) {
      setMergeResult({ error: true, message: err?.message || 'Merge failed.' });
    } finally {
      setMerging(false);
    }
  }

  async function handleLinkTicket() {
    if (!linkTicketUser || !linkTicketNumber.trim()) return;
    setLinkingTicket(true);
    setLinkTicketResult(null);
    try {
      const result = await apiClient<Record<string, any>>(`/users/${linkTicketUser.id}/link-ticket`, {
        method: 'POST',
        body: { ticketNumber: linkTicketNumber.trim() },
      });
      setLinkTicketResult(result);
      fetchAllUsers();
    } catch (err: any) {
      setLinkTicketResult({ error: true, message: err?.message || 'Failed to link ticket.' });
    } finally {
      setLinkingTicket(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Loading members...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>User Management</h1>
          <p style={styles.subtitle}>
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} · Manage roles and accounts
          </p>
        </div>
      </div>

      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '2px solid #e0e0e0' }}>
        <button
          style={{ padding: '0.6rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: activeTab === 'members' ? '#1a6b3c' : '#666', borderBottom: activeTab === 'members' ? '2px solid #1a6b3c' : '2px solid transparent', marginBottom: '-2px' }}
          onClick={() => setActiveTab('members')}
        >
          KDU Members
        </button>
        <button
          style={{ padding: '0.6rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: activeTab === 'all-users' ? '#1a6b3c' : '#666', borderBottom: activeTab === 'all-users' ? '2px solid #1a6b3c' : '2px solid transparent', marginBottom: '-2px' }}
          onClick={() => setActiveTab('all-users')}
        >
          All System Users
        </button>
      </div>

      {/* All System Users Tab */}
      {activeTab === 'all-users' && (
        <div>
          <div style={styles.searchBar}>
            <input
              style={styles.searchInput}
              placeholder="🔍 Search users by name or email..."
              value={allUsersSearch}
              onChange={(e) => setAllUsersSearch(e.target.value)}
              aria-label="Search all users"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')}
              aria-label="Filter by account status"
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: '#fff',
                color: '#333',
                cursor: 'pointer',
              }}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="disabled">Disabled Only</option>
            </select>
            {canEdit && (
              <button
                onClick={() => { setAddUserOpen(true); setAddUserSuccess(null); }}
                style={{ padding: '8px 14px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                ➕ Add User
              </button>
            )}
            <button
              onClick={() => { setMergeModalOpen(true); setMergeSearch(''); setMergeResult(null); }}
              style={{ padding: '8px 14px', backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              🔀 Merge Accounts
            </button>
          </div>

          {addUserSuccess && (
            <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <span>✓ {addUserSuccess}</span>
              <button onClick={() => setAddUserSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '1rem' }} aria-label="Dismiss">✕</button>
            </div>
          )}
          {allUsersLoading ? (
            <div style={styles.loading}>Loading users...</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allUsers
                  .filter((u) => {
                    // Status filter
                    if (statusFilter === 'active' && u.deactivatedAt !== null) return false;
                    if (statusFilter === 'disabled' && u.deactivatedAt === null) return false;
                    // Search filter
                    if (!allUsersSearch.trim()) return true;
                    const q = allUsersSearch.toLowerCase();
                    return u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                  })
                  .map((u) => (
                    <React.Fragment key={u.id}>
                    <tr style={u.deactivatedAt ? { backgroundColor: '#fafafa' } : undefined}>
                      <td style={styles.td}>
                        {u.fullName}
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem', marginLeft: '6px', opacity: 0.6 }}
                          title={`Edit name for ${u.fullName}`}
                          aria-label={`Edit name for ${u.fullName}`}
                          onClick={async () => {
                            const newName = prompt('Enter new full name:', u.fullName);
                            if (!newName || newName.trim() === u.fullName) return;
                            try {
                              await apiClient(`/users/${u.id}/full-name`, { method: 'PATCH', body: { fullName: newName.trim() } });
                              setAllUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, fullName: newName.trim() } : x));
                            } catch (err: any) {
                              alert(err?.message || 'Failed to update name.');
                            }
                          }}
                        >✏️</button>
                        {u.deactivatedAt && (
                          <span style={styles.disabledBadge} title={u.disabledMessage || 'Account disabled'}>
                            Disabled
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {u.email}
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem', marginLeft: '6px', opacity: 0.6 }}
                          title={`Edit login email for ${u.fullName}`}
                          aria-label={`Edit email for ${u.fullName}`}
                          onClick={async () => {
                            const newEmail = prompt('Enter new login email:', u.email);
                            if (!newEmail || newEmail.trim().toLowerCase() === u.email.toLowerCase()) return;
                            try {
                              await apiClient(`/users/${u.id}/email`, { method: 'PATCH', body: { email: newEmail.trim() } });
                              setAllUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, email: newEmail.trim().toLowerCase() } : x));
                            } catch (err: any) {
                              alert(err?.message || 'Failed to update email.');
                            }
                          }}
                        >✏️</button>
                      </td>
                      <td style={styles.td}>
                        {u.id === currentUserId ? (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: u.role === 'Admin' ? '#e8f5e9' : u.role === 'Organizer' ? '#e3f2fd' : '#f5f5f5', color: u.role === 'Admin' ? '#1a6b3c' : u.role === 'Organizer' ? '#1565c0' : '#666' }}>
                            {u.role} (you)
                          </span>
                        ) : canEdit ? (
                          <select
                            value={u.role}
                            disabled={roleSavingId === u.id}
                            onChange={async (e) => {
                              const newRole = e.target.value;
                              if (newRole === u.role) return;
                              setRoleSavingId(u.id);
                              try {
                                await apiClient(`/users/${u.id}/role`, { method: 'PATCH', body: { role: newRole } });
                                setAllUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
                              } catch (err: any) {
                                alert(err?.message || 'Failed to change role.');
                              } finally {
                                setRoleSavingId(null);
                              }
                            }}
                            style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff', cursor: 'pointer' }}
                            aria-label={`Change role for ${u.fullName}`}
                          >
                            <option value="Attendee">Attendee</option>
                            <option value="Delegate">Delegate</option>
                            <option value="Organizer">Organizer</option>
                            <option value="Admin">Admin</option>
                          </select>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: u.role === 'Admin' ? '#e8f5e9' : u.role === 'Organizer' ? '#e3f2fd' : '#f5f5f5', color: u.role === 'Admin' ? '#1a6b3c' : u.role === 'Organizer' ? '#1565c0' : '#666' }}>
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {u.id !== currentUserId && (
                            u.deactivatedAt ? (
                              <button
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, transition: 'background-color 0.15s', color: '#1a6b3c' }}
                                onClick={() => handleEnableUser(u.id)}
                                disabled={enabling === u.id}
                                title="Enable account"
                                aria-label={`Enable ${u.fullName}`}
                              >
                                {enabling === u.id ? '⏳' : '✅'}
                              </button>
                            ) : (
                              <button
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, transition: 'background-color 0.15s', color: '#d32f2f' }}
                                onClick={() => { setDisableModalUser({ id: u.id, fullName: u.fullName }); setDisableMessage(''); }}
                                title="Disable account"
                                aria-label={`Disable ${u.fullName}`}
                              >
                                🚫
                              </button>
                            )
                          )}
                          {u.id !== currentUserId && (
                            <button
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, color: '#1565c0' }}
                              onClick={() => { setLinkTicketUser({ id: u.id, fullName: u.fullName }); setLinkTicketNumber(''); setLinkTicketResult(null); }}
                              title="Link ticket"
                              aria-label={`Link ticket to ${u.fullName}`}
                            >
                              🎫
                            </button>
                          )}
                          <button
                            style={resettingId === u.id ? styles.saveBtnDisabled : styles.saveBtn}
                            disabled={resettingId === u.id}
                            onClick={() => handleResetPassword(u.id)}
                          >
                            {resettingId === u.id ? 'Resetting...' : 'Reset Password'}
                          </button>
                          <button
                            style={checkingId === u.id ? styles.saveBtnDisabled : { ...styles.saveBtn, backgroundColor: '#1565c0' }}
                            disabled={checkingId === u.id}
                            onClick={() => handleCheckProvisioning(u.id)}
                            aria-label={`Check provisioning for ${u.fullName}`}
                          >
                            {checkingId === u.id ? '...' : '🔍 Check'}
                          </button>
                        </div>
                        {resetResult && resetResult.userId === u.id && (
                          <div style={{ marginTop: '6px', padding: '6px 10px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '12px', color: '#1a6b3c', wordBreak: 'break-all' }}>
                            New password: <strong>{resetResult.newPassword}</strong>
                          </div>
                        )}
                      </td>
                    </tr>
                    {provisioningResult && provisioningResult.userId === u.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: '0 16px 12px 16px', borderBottom: '1px solid #eee' }}>
                          <div style={{ padding: '10px 14px', backgroundColor: '#f5f5f5', borderRadius: '6px', fontSize: '11px', border: '1px solid #ddd', position: 'relative', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
                            <button
                              onClick={() => setProvisioningResult(null)}
                              style={{ position: 'absolute', top: '6px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#999', lineHeight: 1, padding: '2px 4px' }}
                              aria-label="Dismiss"
                              title="Dismiss"
                            >✕</button>
                            {JSON.stringify(provisioningResult, null, 2)}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          )}

          {/* Disable Confirmation Modal */}
          {disableModalUser && (
            <div
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => { setDisableModalUser(null); setDisableMessage(''); }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="disable-modal-title"
            >
              <div
                style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '24px', width: '420px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="disable-modal-title" style={{ margin: '0 0 8px', fontSize: '1rem', color: '#333' }}>
                  Disable Account
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
                  Are you sure you want to disable <strong>{disableModalUser.fullName}</strong>? They will not be able to log in.
                </p>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>
                  Custom message (optional, max 500 chars)
                </label>
                <textarea
                  value={disableMessage}
                  onChange={(e) => { if (e.target.value.length <= 500) setDisableMessage(e.target.value); }}
                  placeholder="Reason shown to the user on login attempt..."
                  maxLength={500}
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                  aria-label="Custom disabled message"
                />
                <div style={{ fontSize: '11px', color: '#999', textAlign: 'right', marginTop: '4px' }}>
                  {disableMessage.length}/500
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                  <button
                    onClick={() => { setDisableModalUser(null); setDisableMessage(''); }}
                    style={{ padding: '8px 16px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDisableUser}
                    disabled={disabling}
                    style={{ padding: '8px 16px', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: disabling ? 'not-allowed' : 'pointer', opacity: disabling ? 0.7 : 1 }}
                  >
                    {disabling ? 'Disabling...' : 'Disable Account'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Merge Accounts Modal */}
          {mergeModalOpen && (
            <div
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setMergeModalOpen(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="merge-modal-title"
            >
              <div
                style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '24px', width: '500px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="merge-modal-title" style={{ margin: '0 0 8px', fontSize: '1rem', color: '#333' }}>
                  🔀 Merge Duplicate Accounts
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
                  Search by name to find and merge duplicate accounts. The system keeps the primary account (more registrations, non-synthetic, higher role) and deactivates the duplicate.
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    value={mergeSearch}
                    onChange={(e) => setMergeSearch(e.target.value)}
                    placeholder="Enter name to search (e.g. Christine Wil)..."
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '13px' }}
                    aria-label="Name search for merge"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleMergeAccounts(); }}
                  />
                  <button
                    onClick={handleMergeAccounts}
                    disabled={merging || !mergeSearch.trim()}
                    style={{ padding: '8px 16px', backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: merging || !mergeSearch.trim() ? 'not-allowed' : 'pointer', opacity: merging || !mergeSearch.trim() ? 0.7 : 1 }}
                  >
                    {merging ? 'Merging...' : 'Merge'}
                  </button>
                </div>

                {mergeResult && (
                  <div style={{ padding: '12px 14px', borderRadius: '6px', fontSize: '12px', lineHeight: 1.6, backgroundColor: mergeResult.error ? '#fdecea' : mergeResult.success ? '#e8f5e9' : '#f5f5f5', border: `1px solid ${mergeResult.error ? '#f5c6cb' : mergeResult.success ? '#a5d6a7' : '#ddd'}`, color: '#333' }}>
                    {mergeResult.error ? (
                      <div style={{ color: '#d32f2f', fontWeight: 600 }}>❌ {mergeResult.message}</div>
                    ) : mergeResult.success ? (
                      <>
                        <div style={{ fontWeight: 600, color: '#1a6b3c', marginBottom: '8px' }}>✅ Merge complete</div>
                        <div><strong>Kept:</strong> {mergeResult.kept?.fullName} ({mergeResult.kept?.email})</div>
                        <div><strong>Deactivated:</strong> {mergeResult.deactivated?.fullName} ({mergeResult.deactivated?.email})</div>
                        {mergeResult.log && (
                          <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                            {mergeResult.log.map((entry: string, i: number) => (
                              <li key={i}>{entry}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>{mergeResult.message || 'Result'}</div>
                        {mergeResult.accounts && mergeResult.accounts.map((acc: any, i: number) => (
                          <div key={i}>{acc.fullName} ({acc.email}) — {acc.role} — {acc.registrations} registration(s){acc.memberLinked ? ` — Member: ${acc.memberLinked}` : ''}</div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button
                    onClick={() => setMergeModalOpen(false)}
                    style={{ padding: '8px 16px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Link Ticket Modal */}
          {linkTicketUser && (
            <div
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setLinkTicketUser(null)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="link-ticket-title"
            >
              <div
                style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '24px', width: '420px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="link-ticket-title" style={{ margin: '0 0 8px', fontSize: '1rem', color: '#333' }}>🎫 Link Ticket</h3>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
                  Link <strong>{linkTicketUser.fullName}</strong> to an existing ticket by number.
                </p>
                <input
                  value={linkTicketNumber}
                  onChange={(e) => setLinkTicketNumber(e.target.value)}
                  placeholder="TKT-091"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  aria-label="Ticket number"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLinkTicket(); }}
                />
                {linkTicketResult && (
                  <div style={{ marginTop: '12px', padding: '10px', borderRadius: '6px', fontSize: '12px', backgroundColor: linkTicketResult.error ? '#fdecea' : '#e8f5e9', color: linkTicketResult.error ? '#d32f2f' : '#1a6b3c' }}>
                    {linkTicketResult.error ? `❌ ${linkTicketResult.message}` : `✅ Linked to ${linkTicketResult.guestName} (${linkTicketResult.ticketNumber})`}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                  <button onClick={() => setLinkTicketUser(null)} style={{ padding: '8px 16px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleLinkTicket} disabled={linkingTicket || !linkTicketNumber.trim()} style={{ padding: '8px 16px', backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: linkingTicket || !linkTicketNumber.trim() ? 'not-allowed' : 'pointer', opacity: linkingTicket || !linkTicketNumber.trim() ? 0.7 : 1 }}>
                    {linkingTicket ? 'Linking...' : 'Link Ticket'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KDU Members Tab (existing content) */}
      {activeTab === 'members' && (<>

      {error && (
        <div style={styles.error} role="alert">
          <span style={styles.errorMessage}>{error}</span>
          <div style={styles.errorActions}>
            {error.toLowerCase().includes('network') && (
              <button
                style={styles.retryBtn}
                onClick={retry}
                aria-label="Retry"
              >
                Retry
              </button>
            )}
            <button
              style={styles.dismissBtn}
              onClick={clearError}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div style={styles.searchBar}>
        <input
          style={styles.searchInput}
          placeholder="🔍 Search members by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search members by name"
        />
        {searchQuery && (
          <button
            style={{
              padding: '6px 12px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
            onClick={() => setSearchQuery('')}
          >
            Clear
          </button>
        )}
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Account Status</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredMembers.map((member) => {
            const isLinked = member.userId !== null && member.user != null;
            const isSelf = isLinked && member.userId === currentUserId;
            const isSaving = savingIds.has(member.id);
            const hasPending = pendingChanges.has(member.id);

            return (
              <tr key={member.id} style={isSelf ? styles.disabledRow : undefined}>
                <td style={styles.td}>
                  {member.firstName} {member.lastName}
                  {isSelf && <span style={styles.selfIndicator}>(you)</span>}
                </td>
                <td style={styles.td}>{member.email}</td>
                <td style={styles.td}>
                  {isLinked ? (
                    <span style={styles.statusLinked}>✓ Linked</span>
                  ) : (
                    <span style={styles.statusUnlinked}>No account</span>
                  )}
                </td>
                <td style={styles.td}>
                  {isLinked && member.user ? (
                    <RoleRadioGroup
                      currentRole={member.user.role}
                      selectedRole={pendingChanges.get(member.id) ?? member.user.role}
                      onChange={(role) => setPendingRole(member.id, role)}
                      disabled={isSelf || isSaving}
                      showAdminOption={authUser?.role === 'Admin'}
                      groupId={member.id}
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  {isLinked ? (
                    hasPending ? (
                      canEdit && <button
                        style={isSaving ? styles.saveBtnDisabled : styles.saveBtn}
                        disabled={isSaving}
                        onClick={() => saveRole(member.id, member.userId!)}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    ) : null
                  ) : (
                    canEdit && <button
                      style={styles.createBtn}
                      onClick={() => setCreateModalMember(member)}
                    >
                      Create Account
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredMembers.length === 0 && (
        <p style={styles.emptyState}>
          {searchQuery ? 'No members match the current search.' : 'No members found.'}
        </p>
      )}

      {createModalMember && (
        <CreateAccountModal
          member={createModalMember}
          onSubmit={handleCreateAccount}
          onClose={() => setCreateModalMember(null)}
          loading={createLoading}
        />
      )}

      </>)}

      {/* Add User modal — rendered at top level so it works from the All System Users tab */}
      {addUserOpen && (
        <AddUserModal
          onSubmit={handleAddUser}
          onClose={() => setAddUserOpen(false)}
          loading={addUserLoading}
        />
      )}
    </div>
  );
}
